// ============================================================
// VCS.E2E — End-to-End-Verschlüsselung (Chat-Integration)
// Krypto-Primitiven kommen aus messenger/js/e2e.js (globales E2E-Objekt)
// ============================================================
window.VCS = window.VCS || {};

VCS.E2E = class {

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        // Peers ohne Server-Key — vermeidet wiederholte 404-Anfragen bis Reload
        this._missingKeys = new Set();

        // Cache für has-backup Abfrage (vermeidet doppelte Requests)
        this._hasBackupCache = {};

        // View-Cache
        this._view = {
            keyRestoreOverlay:           $('#keyRestoreOverlay'),
            keyRestorePasswordInput:     $('#keyRestorePasswordInput'),
            keyRestoreError:             $('#keyRestoreError'),
            keyRestoreSubmit:            $('#keyRestoreSubmit'),
            keyBackupSetupOverlay:       $('#keyBackupSetupOverlay'),
            keyBackupSetupPasswordInput: $('#keyBackupSetupPasswordInput'),
            keyBackupSetupError:         $('#keyBackupSetupError'),
            keyBackupSetupSubmit:        $('#keyBackupSetupSubmit'),
            settingsPasswordInput:       $('#settingsPasswordInput'),
            securityPasswordStatus:      $('#securityPasswordStatus'),
            chatTranslateToggle:         $('#chatTranslateToggle'),
        };
    }

    // ── has-backup (gecacht) ──────────────────────────────────

    async fetchHasBackup(phone) {
        if (this._hasBackupCache[phone] !== undefined) {
            return this._hasBackupCache[phone];
        }
        try {
            const res = await fetch(this._apiBase() + '/keys/has-backup/' + encodeURIComponent(phone));
            if (res.ok) {
                const data = await res.json();
                this._hasBackupCache[phone] = !!data.hasBackup;
                return this._hasBackupCache[phone];
            }
        } catch (_) {}
        return false;
    }

    // ── Key-Setup ────────────────────────────────────────────

    async ensureKeyPair(phone) {
        if (typeof E2E === 'undefined') {
            return;
        }
        try {
            const hasKey = await E2E.hasKeyPair(phone);
            if (hasKey) {
                const serverRes = await fetch(this._apiBase() + '/keys/' + encodeURIComponent(phone));
                if (serverRes.ok) {
                    const serverData = await serverRes.json();
                    const raw = serverData.publicKeyJwk;
                    if (raw && typeof raw === 'string' && raw.trimStart().startsWith('{')) {
                        // Server hat gueltigen Key — currentKeyVersion lokal mit Server syncen
                        // (verhindert kv-Drift wenn Server-DB hoehere Version hat als lokal)
                        await E2E.setCurrentKeyVersion(phone, serverData.version || 1);
                        return;
                    }
                    console.warn('[E2E] Server-Key beschädigt, lade neu hoch...');
                } else {
                    console.warn('[E2E] Server-Key fehlt, lade neu hoch...');
                }
                const localPubJwk = await E2E.loadPublicKeyJwk(phone);
                if (localPubJwk) {
                    const reUpRes = await fetch(this._apiBase() + '/keys', {
                        method : 'POST',
                        headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                        body   : JSON.stringify({ publicKeyJwk: E2E.exportPublicKey(localPubJwk) }),
                    });
                    const reUpData = await reUpRes.json();
                    if (reUpData.version) {
                        await E2E.setCurrentKeyVersion(phone, reUpData.version);
                    }
                    return;
                }
                console.warn('[E2E] Kein lokaler Public-Key-Cache, generiere neu...');
            }

            const kp          = await E2E.generateKeyPair();
            const uploadRes   = await fetch(this._apiBase() + '/keys', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({ publicKeyJwk: E2E.exportPublicKey(kp.publicKey) }),
            });
            const uploadData  = await uploadRes.json();
            const newVersion  = uploadData.version || 1;
            await E2E.storePrivateKeyVersion(phone, newVersion, kp.privateKey);
            await E2E.storePublicKeyJwkVersion(phone, newVersion, kp.publicKey);
            await E2E.setCurrentKeyVersion(phone, newVersion);
            console.log('[E2E] Key-Pair generiert und hochgeladen (Version ' + newVersion + ')');
        } catch (e) {
            console.error('[E2E] ensureKeyPair Fehler:', e);
        }
    }

    async handleKeySetup(phone, regPassword, isExisting, serverHasBackup) {
        if (typeof E2E === 'undefined') {
            return;
        }
        try {
            const hasLocal = await E2E.hasKeyPair(phone);
            if (hasLocal) {
                await this.ensureKeyPair(phone);
                if (regPassword) {
                    await this.createBackup(phone, regPassword);
                }
                return;
            }

            let hasBackup = !!serverHasBackup;
            if (!isExisting && !hasBackup) {
                hasBackup = await this.fetchHasBackup(phone);
            }

            if (hasBackup) {
                if (regPassword) {
                    const restored = await this.autoRestore(phone, regPassword);
                    if (restored) {
                        return;
                    }
                }
                const restored = await this.showRestoreDialog(phone, regPassword);
                if (restored) {
                    return;
                }
            }

            await this.ensureKeyPair(phone);
            // Backup ist PFLICHT, sonst entsteht ein verwaister pubKey ohne wiederherstellbaren privKey
            // — siehe project_e2e_key_mismatch_bug.md
            let backupPw = regPassword;
            if (!backupPw) {
                backupPw = await this.showSetBackupPasswordDialog(phone);
            }
            if (backupPw) {
                await this.createBackup(phone, backupPw);
            } else {
                // Fallback: User hat den erzwungenen Dialog irgendwie umgangen → Soft-Warnung
                console.warn('[E2E] Schluessel ohne Backup gespeichert — verwaister pubKey-Risiko!');
                VCS.ui?.showToast?.(t('keyBackupSetupHint'), 'error', 8000);
            }
        } catch (e) {
            console.error('[E2E] handleKeySetup Fehler:', e);
            await this.ensureKeyPair(phone);
        }
    }

    // Erzwingt Passwort-Eingabe fuer E2E-Backup (kein Cancel-Button — verhindert verwaisten pubKey)
    showSetBackupPasswordDialog(phone) {
        return new Promise((resolve) => {
            const { keyBackupSetupOverlay: $overlay, keyBackupSetupPasswordInput: $input,
                    keyBackupSetupError: $error, keyBackupSetupSubmit: $submit } = this._view;

            if (!$overlay.length) {
                resolve(null);
                return;
            }

            $input.val('');
            $error.addClass('hidden');
            $overlay.removeClass('hidden');
            $input[0]?.focus();

            let busy = false;

            const cleanup = (result) => {
                $overlay.addClass('hidden');
                $submit.off('click', onSubmit);
                $input.off('keydown', onKeydown);
                $submit.prop('disabled', false);
                resolve(result);
            };

            const onSubmit = async () => {
                if (busy) return;
                const pw = $input.val().trim();
                if (!pw || pw.length < 4) {
                    $error.removeClass('hidden');
                    $input[0]?.focus();
                    return;
                }
                busy = true;
                $submit.prop('disabled', true);
                cleanup(pw);
            };

            const onKeydown = (e) => { if (e.key === 'Enter') onSubmit(); };

            $submit.on('click', onSubmit);
            $input.on('keydown', onKeydown);
        });
    }

    async createBackup(phone, password) {
        if (typeof E2E === 'undefined') {
            return;
        }
        let privateKey = await E2E.loadPrivateKey(phone);
        if (!privateKey) {
            return;
        }

        let backupBlob;
        try {
            backupBlob = await E2E.encryptPrivateKeyWithPassword(password, privateKey);
        } catch (e) {
            if (e.name === 'InvalidAccessError' || (e instanceof DOMException && e.message.includes('extractable'))) {
                console.warn('[E2E] Key nicht extrahierbar, generiere neu...');
                const kp         = await E2E.generateKeyPair();
                const uploadRes  = await fetch(this._apiBase() + '/keys', {
                    method : 'POST',
                    headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                    body   : JSON.stringify({ publicKeyJwk: E2E.exportPublicKey(kp.publicKey) }),
                });
                const uploadData = await uploadRes.json();
                const newVersion = uploadData.version || 1;
                await E2E.storePrivateKeyVersion(phone, newVersion, kp.privateKey);
                await E2E.storePublicKeyJwkVersion(phone, newVersion, kp.publicKey);
                await E2E.setCurrentKeyVersion(phone, newVersion);
                E2E.clearCache();
                backupBlob = await E2E.encryptPrivateKeyWithPassword(password, kp.privateKey);
            } else {
                throw e;
            }
        }

        const res = await fetch(this._apiBase() + '/keys/backup', {
            method : 'POST',
            headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
            body   : JSON.stringify({ encryptedPrivateKey: JSON.stringify(backupBlob) }),
        });
        if (!res.ok) {
            throw new Error('Backup-Upload fehlgeschlagen: ' + res.status);
        }
        this._hasBackupCache[phone] = true;
        console.log('[E2E] Key-Backup erstellt und hochgeladen');
        VCS.ui?.showToast?.(t('securityPasswordSaved'), 'success', 3000);
    }

    async autoRestore(phone, password) {
        try {
            const res = await fetch(this._apiBase() + '/keys/backup', {
                headers: VCS.chat?.authHeaders(),
            });
            if (!res.ok) {
                return false;
            }
            const data       = await res.json();
            const privateKey = await E2E.decryptPrivateKeyFromBackup(password, data.encryptedPrivateKey);
            await E2E.storePrivateKey(phone, privateKey);

            const pubRes = await fetch(this._apiBase() + '/keys/' + encodeURIComponent(phone));
            if (pubRes.ok) {
                const pubData        = await pubRes.json();
                const currentVersion = pubData.version || 1;
                const pubJwk         = typeof pubData.publicKeyJwk === 'string'
                    ? JSON.parse(pubData.publicKeyJwk) : pubData.publicKeyJwk;
                await E2E.storePublicKeyJwk(phone, pubJwk);
                await E2E.storePrivateKeyVersion(phone, currentVersion, privateKey);
                await E2E.storePublicKeyJwkVersion(phone, currentVersion, pubJwk);
                await E2E.setCurrentKeyVersion(phone, currentVersion);
            }

            if (data.backups?.length > 0) {
                for (const backup of data.backups) {
                    if (!backup.encryptedPrivateKey) {
                        continue;
                    }
                    try {
                        const oldKey = await E2E.decryptPrivateKeyFromBackup(password, backup.encryptedPrivateKey);
                        await E2E.storePrivateKeyVersion(phone, backup.version, oldKey);
                    } catch (_) {
                        console.warn('[E2E] Key-Version', backup.version, 'konnte nicht wiederhergestellt werden');
                    }
                }
                await E2E.storePrivateKey(phone, privateKey);
            }

            console.log('[E2E] Private Key auto-wiederhergestellt');
            VCS.ui?.showToast?.(t('keyRestoreSuccess'), 'success', 3000);
            return true;
        } catch (e) {
            console.warn('[E2E] Auto-Restore fehlgeschlagen (falsches Passwort?):', e.message);
            return false;
        }
    }

    // showRestoreDialog: Existing-Backup mit User-PW entschluesseln und privKey storen.
    // options.public === true: nutzt /keys/backup-public (no-auth) statt /keys/backup —
    //   fuer Pre-Login-PW-Check vor SMS-Versand. Bei Erfolg resolved mit dem PW-String,
    //   damit der Caller es in den send-code-Payload packen kann.
    // Default-Mode (options.public !== true): bestehendes Verhalten, resolved boolean.
    showRestoreDialog(phone, prefillPassword, options = {}) {
        return new Promise((resolve) => {
            const { keyRestoreOverlay: $overlay, keyRestorePasswordInput: $input,
                    keyRestoreError: $error, keyRestoreSubmit: $submit } = this._view;

            if (!$overlay.length) {
                resolve(false);
                return;
            }

            const isPublic = options && options.public === true;

            $input.val(prefillPassword || '');
            $error.addClass('hidden');
            $overlay.removeClass('hidden');
            $input[0]?.focus();

            let busy = false;

            const cleanup = (result) => {
                $overlay.addClass('hidden');
                $submit.off('click', onSubmit);
                $input.off('keydown', onKeydown);
                $submit.prop('disabled', false);
                resolve(result);
            };

            const onSubmit = async () => {
                if (busy) {
                    return;
                }
                const pw = $input.val().trim();
                if (!pw) {
                    $input[0]?.focus();
                    return;
                }
                busy = true;
                $submit.prop('disabled', true);
                $error.addClass('hidden');

                try {
                    const url = isPublic
                        ? this._apiBase() + '/keys/backup-public/' + encodeURIComponent(phone)
                        : this._apiBase() + '/keys/backup';
                    const headers = isPublic ? {} : VCS.chat?.authHeaders();
                    const res = await fetch(url, { headers });
                    if (!res.ok) {
                        // 404 = kein Backup; jeder andere Status = Server-Fehler.
                        // In beiden Faellen Auth-Fail: User darf nicht ohne validiertes PW weiter.
                        console.warn('[E2E] showRestoreDialog: backup-fetch fehlgeschlagen', res.status, 'public=', isPublic);
                        $error.removeClass('hidden');
                        busy = false;
                        $submit.prop('disabled', false);
                        return;
                    }
                    const data       = await res.json();
                    const privateKey = await E2E.decryptPrivateKeyFromBackup(pw, data.encryptedPrivateKey);
                    await E2E.storePrivateKey(phone, privateKey);

                    const pubRes = await fetch(this._apiBase() + '/keys/' + encodeURIComponent(phone));
                    if (pubRes.ok) {
                        const pubData = await pubRes.json();
                        await E2E.storePublicKeyJwk(phone,
                            typeof pubData.publicKeyJwk === 'string'
                                ? JSON.parse(pubData.publicKeyJwk)
                                : pubData.publicKeyJwk
                        );
                    }

                    console.log('[E2E] Private Key aus Backup wiederhergestellt');
                    if (!isPublic) {
                        VCS.ui?.showToast?.(t('keyRestoreSuccess'), 'success', 3000);
                    }
                    cleanup(isPublic ? pw : true);
                } catch (e) {
                    console.error('[E2E] Restore-Fehler:', e);
                    $error.removeClass('hidden');
                    busy = false;
                    $submit.prop('disabled', false);
                    $input[0]?.focus();
                    $input[0]?.select();
                }
            };

            const onKeydown = (e) => { if (e.key === 'Enter') { onSubmit(); } };

            $submit.on('click', onSubmit);
            $input.on('keydown', onKeydown);
        });
    }

    async savePassword() {
        if (typeof E2E === 'undefined' || !VCS.state.chatMyPhone) {
            return;
        }
        const pw = this._view.settingsPasswordInput.val().trim();
        if (!pw) {
            VCS.ui?.showToast?.(t('securityPasswordRequired'), 'error', 3000);
            this._view.settingsPasswordInput[0]?.focus();
            return;
        }
        try {
            const privateKey = await E2E.loadPrivateKey(VCS.state.chatMyPhone);
            if (!privateKey) {
                VCS.ui?.showToast?.(t('e2eNoKey'), 'error', 3000);
                return;
            }
            await this.createBackup(VCS.state.chatMyPhone, pw);
            this._view.settingsPasswordInput.val('');
            this.updateSecurityPasswordStatus();
        } catch (e) {
            console.error('[E2E] savePassword Fehler:', e);
            VCS.ui?.showToast?.(t('securityPasswordError'), 'error', 4000);
        }
    }

    async updateSecurityPasswordStatus() {
        const $el = this._view.securityPasswordStatus;
        if (!$el.length || !VCS.state.chatMyPhone) {
            return;
        }
        const hasBackup = await this.fetchHasBackup(VCS.state.chatMyPhone);
        $el.text(t(hasBackup ? 'securityPasswordActive' : 'securityPasswordNone'))
           .css('color', hasBackup ? 'var(--color-success, #22c55e)' : 'var(--text-tertiary)');
    }

    // ── Key-Ableitung ────────────────────────────────────────

    async getOrDeriveKey(chatId, versionInfo) {
        if (typeof E2E === 'undefined') {
            return null;
        }
        const chat = VCS.state.chatChats.find(c => c.id === chatId);
        if (!chat) {
            return null;
        }
        const peerPhone = VCS.chat?.peerPhone?.(chat);
        if (!peerPhone) {
            return null;
        }

        if (this._missingKeys.has(peerPhone)) {
            return null;
        }

        try {
            if (versionInfo) {
                return await this._deriveKeyVersioned(peerPhone, versionInfo);
            }
            return await this._deriveKeyCurrent(peerPhone);
        } catch (e) {
            console.error('[E2E] deriveKey Fehler:', e);
            return null;
        }
    }

    // ── Translate-Toggle ─────────────────────────────────────

    async toggleTranslate(chatId) {
        const $toggle = this._view.chatTranslateToggle;
        if (!$toggle.length) {
            return;
        }

        // Plan-Eigenschaft statt Plan-Name (2026-08-30): Uebersetzung gibt es ab
        // dem ersten zahlenden Tarif. Der Trial hebt serverseitig auf einen
        // zahlenden Tarif und faellt damit von selbst richtig heraus.
        if (!VCS.state.chatPlanPaid) {
            $toggle.attr('aria-checked', 'false');
            VCS.ui?.showToast?.(t('translateProOnly'), 'error', 5000);
            return;
        }

        // Source of truth: VCS.state.chatE2EEnabled — translate ON = e2e OFF.
        const currentE2e = !!VCS.state.chatE2EEnabled[chatId];
        const translateOn = !currentE2e; // current UI state (was)
        const newTranslateOn = !translateOn;
        const e2eEnabled = !newTranslateOn;

        // Optimistisches UI-Update — Button reagiert sofort, server bestätigt.
        $toggle.attr('aria-checked', String(newTranslateOn));

        try {
            const res = await fetch(this._apiBase() + '/chats/e2e', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({ chatId, enabled: e2eEnabled }),
            });
            if (res.ok) {
                VCS.state.chatE2EEnabled[chatId] = e2eEnabled;
                this.syncCaptionsFromChat();
                VCS.ui?.showToast?.(
                    t(newTranslateOn ? 'autoTranslateOn' : 'autoTranslateOff') + ' — ' +
                    t(newTranslateOn ? 'autoTranslateE2EWarning' : 'autoTranslateE2ERestored'),
                    'info', 5000
                );
                if (e2eEnabled) {
                    await this.getOrDeriveKey(chatId);
                }
            } else {
                // Rollback bei Server-Fehler.
                $toggle.attr('aria-checked', String(translateOn));
            }
        } catch (e) {
            console.error('[E2E] toggleTranslate Fehler:', e);
            $toggle.attr('aria-checked', String(translateOn));
        }
    }

    updateTranslateToggle() {
        const $toggle = this._view.chatTranslateToggle;
        if (!$toggle.length) {
            return;
        }

        if (!VCS.state.chatPlanPaid) {
            $toggle.attr({ 'aria-checked': 'false', 'disabled': 'disabled' })
                   .data('tooltip-i18n', 'translateProOnly');
            return;
        }

        $toggle.removeAttr('disabled')
               .data('tooltip-i18n', 'realTimeTranslation');

        const e2eActive = !!VCS.state.chatE2EEnabled[VCS.state.chatActiveChatId];
        const translateOn = !e2eActive;
        $toggle.attr('aria-checked', String(translateOn));
        $toggle.attr('title', t(translateOn ? 'autoTranslateOn' : 'autoTranslateOff'));
    }

    syncCaptionsFromChat() {
        let chatId = VCS.state.chatActiveChatId;
        if (!chatId && VCS.state.directCallPeerPhone) {
            const chat = VCS.state.chatChats.find(c =>
                VCS.chat?.peerPhone?.(c) === VCS.state.directCallPeerPhone
            );
            if (chat) {
                chatId = chat.id;
            }
        }

        const newVal = chatId ? !VCS.state.chatE2EEnabled[chatId] : false;
        if (newVal === VCS.state.captionsEnabled) {
            return;
        }

        VCS.state.captionsEnabled = newVal;
        VCS.captions?.sendConfig?.();

        if (!VCS.state.captionsEnabled) {
            VCS.captions?.releasePipeline?.();
            VCS.captions?.clearTranslated?.();
        } else if (
            VCS.state.sessionState.phase === 'call' &&
            VCS.i18n?.isTranslationSupported?.(VCS.i18n.normalizeLang(VCS.state.currentLang))
        ) {
            VCS.captions?.startAutoCapture?.().catch(() => {});
        }

        VCS.captions?.updateFabUI?.();
    }

    // ── Entschlüsselung ──────────────────────────────────────

    async decryptMessage(msg) {
        if (!msg.encrypted || !msg.iv) {
            return;
        }
        try {
            const versionInfo = this._buildVersionInfo(msg);
            const sharedKey   = await this.getOrDeriveKey(msg.chatId, versionInfo);
            if (!sharedKey) {
                return;
            }

            if (msg.originalText && !msg.audioUrl && !msg.mediaUrl) {
                const decrypted = await E2E.decryptText(sharedKey, msg.originalText, msg.iv);
                if (decrypted !== null) {
                    msg._decryptedText = decrypted;
                }
            }

            if (msg.originalText && (msg.mediaUrl || msg.audioUrl)) {
                msg._decryptedText = await this._decryptCaption(sharedKey, msg);
            }

            if (VCS.state.chatActiveChatId === msg.chatId) {
                VCS.chatMessages?.renderChatMessages?.();
                VCS.chatMessages?.scrollToBottom?.();
            }

            if (msg._decryptedText) {
                const chat = VCS.state.chatChats.find(c => c.id === msg.chatId);
                if (chat && chat.lastMessageAt && msg.timestamp >= chat.lastMessageAt) {
                    chat.lastMessageText      = msg._decryptedText;
                    chat.lastMessageEncrypted = 0;
                    if (VCS.nav?.isChatListVisible?.()) {
                        VCS.chatMessages?.renderChatList?.();
                    }
                }
            }
        } catch (e) {
            console.error('[E2E] decryptMessage Fehler:', e);
        }
    }

    async decryptPreviews() {
        if (typeof E2E === 'undefined') {
            return;
        }
        const isBase64 = (s) => /^[A-Za-z0-9+/\-_=\s]+$/.test(s);
        const encrypted = VCS.state.chatChats.filter(c =>
            c.lastMessageEncrypted && c.lastMessageText && c.lastMessageIv
            && isBase64(c.lastMessageText) && isBase64(c.lastMessageIv)
        );
        if (encrypted.length === 0) {
            return;
        }

        let changed = false;
        for (const chat of encrypted) {
            try {
                const sharedKey = await this.getOrDeriveKey(chat.id);
                if (!sharedKey) {
                    continue;
                }
                const decrypted = await E2E.decryptText(sharedKey, chat.lastMessageText, chat.lastMessageIv);
                if (decrypted !== null) {
                    chat.lastMessageText      = decrypted;
                    chat.lastMessageEncrypted = 0;
                    changed = true;
                }
            } catch (_) {}
        }
        if (changed) {
            VCS.chatMessages?.renderChatList?.();
        }
    }

    async decryptAllMessages(chatId) {
        if (typeof E2E === 'undefined') {
            return;
        }
        const msgs = VCS.state.chatMessages[chatId];
        if (!msgs) {
            return;
        }
        const encrypted = msgs.filter(m => m.encrypted && !m._decryptedText && !m._decryptFailed);
        if (encrypted.length === 0) {
            return;
        }

        // Nach Key-Version gruppieren → minimale Key-Ableitungen
        const groups = new Map();
        for (const msg of encrypted) {
            const sv  = msg.keyVersion || 1;
            const rv  = msg.recipientKeyVersion || 1;
            const key = `${sv}:${rv}:${msg.senderPhone || ''}`;
            if (!groups.has(key)) {
                groups.set(key, { sv, rv, senderPhone: msg.senderPhone, msgs: [] });
            }
            groups.get(key).msgs.push(msg);
        }

        let changed = false;
        for (const [, group] of groups) {
            const versionInfo = (group.sv > 1 || group.rv > 1) ? {
                senderVersion:    group.sv,
                recipientVersion: group.rv,
                senderPhone:      group.senderPhone,
            } : null;
            const sharedKey = await this.getOrDeriveKey(chatId, versionInfo);
            if (!sharedKey) {
                continue;
            }
            for (const msg of group.msgs) {
                if (!msg.iv) {
                    continue;
                }
                const decrypted = await this._decryptMsgText(sharedKey, msg);
                if (decrypted !== null) {
                    msg._decryptedText = decrypted;
                    changed = true;
                }
            }
        }

        if (changed && VCS.state.chatActiveChatId === chatId) {
            VCS.chatMessages?.renderChatMessages?.();
            VCS.chatMessages?.scrollToBottom?.();
        }
    }

    async decryptAndPlayAudio(messageId, type, url, msg) {
        try {
            // Bei type='translated' wird mit translatedAudioIv entschluesselt (anderer IV als das Original-Audio)
            const audioIv = type === 'translated' ? msg.translatedAudioIv : msg.iv;
            // Entschluesselte Kopie aus dem lokalen Cache (IndexedDB), sonst laden + entschluesseln + speichern
            let objectUrl = await VCS.mediaCache?.getCached?.(url, { decryptedOnly: true });
            if (!objectUrl) {
                const vInfo     = this._buildVersionInfo(msg);
                const sharedKey = await this.getOrDeriveKey(msg.chatId, vInfo);
                if (!sharedKey) {
                    return;
                }
                const resp = await fetch(url);
                if (!resp.ok) {
                    return;
                }
                const encryptedBlob = await resp.blob();
                const decryptedBlob = await E2E.decryptBlob(sharedKey, encryptedBlob, audioIv, 'audio/mp4');
                if (!decryptedBlob) {
                    return;
                }
                objectUrl = (await VCS.mediaCache?.put?.(url, decryptedBlob, { encrypted: true, mimeType: 'audio/mp4' }))
                    || URL.createObjectURL(decryptedBlob);
            }
            if (!VCS.state.chatAudioPlayers[messageId]) {
                VCS.state.chatAudioPlayers[messageId] = {};
            }
            const audio = new Audio(objectUrl);
            VCS.state.chatAudioPlayers[messageId][type] = audio;

            audio.ontimeupdate = () => {
                const progress = audio.duration ? (audio.currentTime / audio.duration) * 100 : 0;
                VCS.chatVoice?.updateWaveformProgress?.(messageId, type, progress);
                const durEl = document.querySelector(`[data-voice-dur="${messageId}-${type}"]`);
                if (durEl) {
                    const remaining = (audio.duration - audio.currentTime) * 1000;
                    durEl.textContent = VCS.chatVoice?.formatDuration?.(remaining > 0 ? remaining : 0) ?? '';
                }
            };
            audio.onended = () => {
                VCS.state.chatCurrentlyPlaying = null;
                VCS.chatVoice?.updateVoiceUI?.(messageId, type, false);
                VCS.chatVoice?.updateWaveformProgress?.(messageId, type, 0);
            };
            audio.play().catch(() => {});
            VCS.state.chatCurrentlyPlaying = { messageId, type };
            VCS.chatVoice?.updateVoiceUI?.(messageId, type, true);
        } catch (e) {
            console.error('[E2E] decryptAndPlayAudio Fehler:', e);
        }
    }

    async getDecryptedMediaUrl(msg) {
        if (!msg.encrypted || !msg.iv || typeof E2E === 'undefined') {
            return msg.mediaUrl;
        }
        if (msg._decryptedMediaUrl) {
            return msg._decryptedMediaUrl;
        }
        if (msg._decryptFailed) {
            return msg.mediaUrl;
        }
        // Nur getCached (nie get): get() faellt bei Miss auf fetch+put zurueck und wuerde
        // verschluesselte Bytes als "fertig" speichern. decryptedOnly ignoriert solche Altlasten.
        try {
            const cachedUrl = await VCS.mediaCache?.getCached?.(msg.mediaUrl, { decryptedOnly: true });
            if (cachedUrl) {
                msg._decryptedMediaUrl = cachedUrl;
                return cachedUrl;
            }
            const vInfo     = this._buildVersionInfo(msg);
            const sharedKey = await this.getOrDeriveKey(msg.chatId, vInfo);
            if (!sharedKey) {
                return msg.mediaUrl;
            }
            const resp = await fetch(msg.mediaUrl);
            if (!resp.ok) {
                return msg.mediaUrl;
            }
            const encryptedBlob = await resp.blob();
            const mimeMap = { image: 'image/jpeg', video: 'video/mp4', music: 'audio/mpeg' };
            const decryptedBlob = await E2E.decryptBlob(
                sharedKey, encryptedBlob, msg.iv,
                mimeMap[msg.mediaType] || 'application/octet-stream'
            );
            if (!decryptedBlob) {
                msg._decryptFailed = true;
                return msg.mediaUrl;
            }
            // Entschluesselt im lokalen Cache ablegen (Videos auf Apple-Geraeten
            // lehnt put() ab -> dann wie bisher nur im Speicher)
            const storedUrl = await VCS.mediaCache?.put?.(msg.mediaUrl, decryptedBlob, { encrypted: true });
            msg._decryptedMediaUrl = storedUrl || URL.createObjectURL(decryptedBlob);
            return msg._decryptedMediaUrl;
        } catch (e) {
            console.error('[E2E] decryptMedia Fehler:', e);
            msg._decryptFailed = true;
            return msg.mediaUrl;
        }
    }

    // Entschluesselt translatedVideoUrl (eigener IV: translatedVideoIv)
    async getDecryptedTranslatedVideoUrl(msg) {
        if (!msg?.translatedVideoUrl) return null;
        if (!msg.translatedVideoIv || typeof E2E === 'undefined') {
            return msg.translatedVideoUrl;
        }
        if (msg._decryptedTranslatedVideoUrl) return msg._decryptedTranslatedVideoUrl;
        try {
            const cachedUrl = await VCS.mediaCache?.getCached?.(msg.translatedVideoUrl, { decryptedOnly: true });
            if (cachedUrl) {
                msg._decryptedTranslatedVideoUrl = cachedUrl;
                return cachedUrl;
            }
            const vInfo = this._buildVersionInfo(msg);
            const sharedKey = await this.getOrDeriveKey(msg.chatId, vInfo);
            if (!sharedKey) return msg.translatedVideoUrl;
            const resp = await fetch(msg.translatedVideoUrl);
            if (!resp.ok) return msg.translatedVideoUrl;
            const encBlob = await resp.blob();
            const decBlob = await E2E.decryptBlob(sharedKey, encBlob, msg.translatedVideoIv, 'video/mp4');
            if (!decBlob) return msg.translatedVideoUrl;
            const storedUrl = await VCS.mediaCache?.put?.(msg.translatedVideoUrl, decBlob, { encrypted: true });
            msg._decryptedTranslatedVideoUrl = storedUrl || URL.createObjectURL(decBlob);
            return msg._decryptedTranslatedVideoUrl;
        } catch (e) {
            console.error('[E2E] decryptTranslatedVideo Fehler:', e);
            return msg.translatedVideoUrl;
        }
    }

    // ── Init ─────────────────────────────────────────────────

    init() {
        // Kein eigenständiger Setup — wird von anderen Modulen aufgerufen
    }

    // ── Private Methoden ─────────────────────────────────────

    _apiBase() {
        return location.origin + '/api';
    }

    _buildVersionInfo(msg) {
        const sv = msg.keyVersion || 1;
        const rv = msg.recipientKeyVersion || 1;
        return (sv > 1 || rv > 1)
            ? { senderVersion: sv, recipientVersion: rv, senderPhone: msg.senderPhone }
            : null;
    }

    async _deriveKeyCurrent(peerPhone) {
        const cached = E2E.getCachedKey(peerPhone);
        if (cached?._versionInfo) {
            return cached;
        }
        const privateKey = await E2E.loadPrivateKey(VCS.state.chatMyPhone);
        if (!privateKey) {
            console.warn('[E2E] Kein Private Key gefunden');
            return null;
        }
        const myVersion = await E2E.getCurrentKeyVersion(VCS.state.chatMyPhone);
        const res = await fetch(this._apiBase() + '/keys/' + encodeURIComponent(peerPhone), {
            headers: VCS.chat?.authHeaders(),
        });
        if (!res.ok) {
            this._missingKeys.add(peerPhone);
            console.warn('[E2E] Peer-Key nicht gefunden für', peerPhone, '— überspringe bis Reload');
            return null;
        }
        const data    = await res.json();
        const rawKey  = data.publicKeyJwk;
        const peerVer = data.version || 1;

        if (!rawKey || (typeof rawKey === 'string' && !rawKey.trimStart().startsWith('{'))) {
            console.warn('[E2E] Ungültiger Peer-Key für', peerPhone);
            return null;
        }

        const peerPublicKey = await E2E.importPublicKey(rawKey);
        const keyVInfo      = { senderVersion: myVersion, recipientVersion: peerVer };
        const sharedKey     = await E2E.deriveSharedKey(privateKey, peerPublicKey, VCS.state.chatMyPhone, peerPhone, keyVInfo);
        sharedKey._versionInfo = { senderVersion: myVersion, recipientVersion: peerVer };
        E2E.cacheKey(peerPhone, sharedKey, myVersion, peerVer);
        return sharedKey;
    }

    async _deriveKeyVersioned(peerPhone, versionInfo) {
        const { senderVersion, recipientVersion, senderPhone } = versionInfo;
        const iAmSender   = senderPhone === VCS.state.chatMyPhone;
        const myVersion   = iAmSender ? senderVersion : recipientVersion;
        const peerVersion = iAmSender ? recipientVersion : senderVersion;

        const cached = E2E.getCachedKey(peerPhone, myVersion, peerVersion);
        if (cached) {
            return cached;
        }

        let privateKey = await E2E.loadPrivateKeyVersion(VCS.state.chatMyPhone, myVersion);
        const useFallback = !privateKey;

        if (useFallback) {
            privateKey = await E2E.loadPrivateKey(VCS.state.chatMyPhone);
            if (!privateKey) {
                return null;
            }
        }

        const url = useFallback
            ? this._apiBase() + '/keys/' + encodeURIComponent(peerPhone)
            : this._apiBase() + '/keys/' + encodeURIComponent(peerPhone) + '/' + peerVersion;

        let res = await fetch(url, { headers: VCS.chat?.authHeaders() });
        if (!res.ok && !useFallback) {
            res = await fetch(this._apiBase() + '/keys/' + encodeURIComponent(peerPhone), {
                headers: VCS.chat?.authHeaders(),
            });
            if (!res.ok) {
                return null;
            }
        }

        const data = await res.json();
        if (!data.publicKeyJwk) {
            return null;
        }

        const peerPublicKey = await E2E.importPublicKey(data.publicKeyJwk);
        const sharedKey     = await E2E.deriveSharedKey(
            privateKey, peerPublicKey, VCS.state.chatMyPhone, peerPhone,
            useFallback ? undefined : { senderVersion, recipientVersion }
        );
        E2E.cacheKey(peerPhone, sharedKey, myVersion, peerVersion);
        return sharedKey;
    }

    async _decryptCaption(sharedKey, msg) {
        try {
            const parsed = JSON.parse(msg.originalText);
            if (parsed.ct && parsed.iv) {
                const dec = await E2E.decryptText(sharedKey, parsed.ct, parsed.iv);
                return dec;
            }
        } catch (_) {}
        return await E2E.decryptText(sharedKey, msg.originalText, msg.iv);
    }

    async _decryptMsgText(sharedKey, msg) {
        if (msg.originalText && !msg.audioUrl && !msg.mediaUrl) {
            return await E2E.decryptText(sharedKey, msg.originalText, msg.iv);
        }
        if (msg.originalText && (msg.mediaUrl || msg.audioUrl)) {
            return await this._decryptCaption(sharedKey, msg);
        }
        return null;
    }
};
